import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, varietyCodeSchema, weekBinsSchema, weekNumberSchema, } from './common.js';
/**
 * `submitted`: sent and accepted by the pack house.
 * `edited`: submitted, then changed by the grower.
 * `queued`: saved on the phone, waiting for signal. Client-side only; the API never returns it.
 */
export const estimateStatusSchema = z.enum(['submitted', 'edited', 'queued']);
/** One variety's expected bins for the week. */
export const estimateLineSchema = z.object({
    varietyCode: varietyCodeSchema,
    bins: weekBinsSchema,
});
const linesSchema = z
    .array(estimateLineSchema)
    .min(1, 'Choose at least one variety.')
    .refine((lines) => new Set(lines.map((l) => l.varietyCode)).size === lines.length, 'Each variety may appear only once.');
/** Identifies which week an estimate is for. `weekStart` is always a Monday. */
export const estimateWeekSchema = z.object({
    seasonId: idSchema,
    weekNumber: weekNumberSchema,
    weekStart: isoDateSchema,
});
/** A stored estimate as the API returns it. */
export const estimateSchema = estimateWeekSchema.extend({
    id: idSchema,
    farmId: idSchema,
    status: estimateStatusSchema,
    lines: linesSchema,
    submittedAt: isoDateTimeSchema,
    submittedBy: z.string().min(1),
    updatedAt: isoDateTimeSchema,
});
/**
 * Create. The client supplies the id so a queued submission can be retried without duplicating.
 * `capturedAt` is when the grower pressed Submit, which may be long before it reaches the API.
 */
export const createEstimateRequestSchema = estimateWeekSchema.extend({
    id: idSchema,
    farmId: idSchema,
    lines: linesSchema,
    capturedAt: isoDateTimeSchema,
});
/** Update replaces the full set of lines. Removing a variety is simply omitting it. */
export const updateEstimateRequestSchema = z.object({
    lines: linesSchema,
    capturedAt: isoDateTimeSchema,
});
export const listEstimatesQuerySchema = z.object({
    farmId: idSchema.optional(),
    seasonId: idSchema.optional(),
    /** Per farm. The design shows the last 3 to 4. */
    limit: z.coerce.number().int().min(1).max(52).default(4),
});
export const estimateListResponseSchema = z.object({
    estimates: z.array(estimateSchema),
});
/* ---------- pure helpers on the shape ---------- */
export function lineTotal(line) {
    return line.bins.reduce((sum, n) => sum + n, 0);
}
export function estimateTotal(estimate) {
    return estimate.lines.reduce((sum, line) => sum + lineTotal(line), 0);
}
/** Expected bins per day summed across every variety. Index 0 is Monday, 5 is Saturday. */
export function dayTotals(lines) {
    const onDay = (i) => lines.reduce((sum, line) => sum + (line.bins[i] ?? 0), 0);
    return [onDay(0), onDay(1), onDay(2), onDay(3), onDay(4), onDay(5)];
}
//# sourceMappingURL=estimate.js.map